import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { messages, LOCALES, DEFAULT_LOCALE } from './messages';

// Locale preference is non-sensitive UI state; localStorage is the correct
// choice (it's per-device, survives reloads, and contains no PII or credentials).
const STORAGE_KEY = 'klymart_locale';
const IS_DEV = process.env.NODE_ENV === 'development';

function devWarn(message, err) {
  if (IS_DEV && typeof console !== 'undefined' && console?.debug) {
    console.debug(message, err?.message);
  }
}

function safeGetItem(key) {
  try {
    return localStorage.getItem(key);
  } catch (err) {
    // localStorage can be unavailable (private mode, disabled cookies, SSR).
    devWarn('[i18n] localStorage.getItem unavailable:', err);
    return null;
  }
}

function safeSetItem(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch (err) {
    devWarn('[i18n] localStorage.setItem unavailable:', err);
  }
}

function detectBrowserLocale() {
  try {
    const nav = (navigator?.language || '').toLowerCase();
    return nav.startsWith('de') ? 'de' : null;
  } catch {
    return null;
  }
}

function getInitialLocale() {
  const stored = safeGetItem(STORAGE_KEY);
  if (stored && LOCALES.includes(stored)) return stored;
  return detectBrowserLocale() || DEFAULT_LOCALE;
}

function resolvePath(obj, path) {
  const parts = String(path).split('.');
  let cur = obj;
  for (const p of parts) {
    if (cur && typeof cur === 'object' && p in cur) {
      cur = cur[p];
    } else {
      return path; // fall back to the key itself
    }
  }
  return cur;
}

function interpolate(template, vars) {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, k) =>
    vars[k] !== undefined ? String(vars[k]) : '',
  );
}

const I18nContext = createContext({
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
  t: () => '',
  m: messages[DEFAULT_LOCALE],
});

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(getInitialLocale);

  const setLocale = useCallback((next) => {
    if (!LOCALES.includes(next)) return;
    setLocaleState(next);
    safeSetItem(STORAGE_KEY, next);
  }, []);

  useEffect(() => {
    try {
      document.documentElement.lang = locale;
    } catch (err) {
      devWarn('[i18n] could not set <html lang>:', err);
    }
  }, [locale]);

  const m = useMemo(
    () => messages[locale] || messages[DEFAULT_LOCALE],
    [locale],
  );

  const t = useCallback(
    (path, vars) => {
      const value = resolvePath(m, path);
      return typeof value === 'string' ? interpolate(value, vars) : value;
    },
    [m],
  );

  const value = useMemo(
    () => ({ locale, setLocale, t, m }),
    [locale, setLocale, t, m],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}
